// Merged cells.
//
// A merge is its rectangle, nothing more — no id, no title, no formulas of
// its own. The anchor (top-left) keeps the one value/style that renders;
// every other cell it covers is simply never written (see ui/store.ts's
// write paths), so a formula that happens to reference a covered cell reads
// it as blank with no change needed anywhere in the engine.

import type { Axis, IndexMap } from './structure';
import type { Merge, Sheet } from './types';

/** The merge covering (row,col), if any — its anchor included. */
export function mergeAt(sheet: Sheet, row: number, col: number): Merge | undefined {
  return sheet.merges?.find((m) => row >= m.r1 && row <= m.r2 && col >= m.c1 && col <= m.c2);
}

export function isMergeAnchor(m: Merge, row: number, col: number): boolean {
  return row === m.r1 && col === m.c1;
}

/** Covered by a merge, but not the one cell of it that actually renders. */
export function isCoveredNonAnchor(sheet: Sheet, row: number, col: number): boolean {
  const m = mergeAt(sheet, row, col);
  return m !== undefined && !isMergeAnchor(m, row, col);
}

/**
 * The existing merge this rectangle overlaps, if any — EXACT match included,
 * so a caller can tell "re-merging the same range" (a no-op) apart from a
 * genuine conflict with a different merge.
 */
export function overlapsAny(
  merges: readonly Merge[] | undefined,
  r1: number,
  c1: number,
  r2: number,
  c2: number
): Merge | undefined {
  return merges?.find((m) => m.r1 <= r2 && m.r2 >= r1 && m.c1 <= c2 && m.c2 >= c1);
}

/**
 * The smallest rectangle holding (r1,c1)-(r2,c2) that cuts through no merge:
 * grown until every merge it touches sits wholly inside it. Growing can pull
 * in another merge, so it repeats until nothing changes.
 */
export function growToMerges(
  merges: readonly Merge[] | undefined,
  rect: { r1: number; c1: number; r2: number; c2: number }
): { r1: number; c1: number; r2: number; c2: number } {
  let { r1, c1, r2, c2 } = rect;
  for (let grew = true; grew; ) {
    grew = false;
    for (const m of merges ?? []) {
      if (m.r1 > r2 || m.r2 < r1 || m.c1 > c2 || m.c2 < c1) continue;
      if (m.r1 < r1 || m.r2 > r2 || m.c1 < c1 || m.c2 > c2) {
        r1 = Math.min(r1, m.r1);
        r2 = Math.max(r2, m.r2);
        c1 = Math.min(c1, m.c1);
        c2 = Math.max(c2, m.c2);
        grew = true;
      }
    }
  }
  return { r1, c1, r2, c2 };
}

/**
 * Remap every merge through a structural edit on ONE axis — insert, delete or
 * move a run of rows/columns. Unlike an anchor-only entity (a database, see
 * structure.ts's `moveDatabases`), a merge SPANS a range on this axis, so an
 * edit in the middle of that span has to shrink or grow it, not just remap
 * or drop the whole thing.
 *
 * Dropped only when every row/col of the span on THIS axis vanishes — a
 * merge that shrinks to 1 wide on one axis (because most of its rows/cols
 * were deleted) is still a real 1xN merge, not nothing. A merge that has
 * independently ground down to a literal 1x1 on BOTH axes (across separate
 * row and column edits) is caught centrally by `mergeAt` ignoring it, rather
 * than chased at every mutation site.
 */
export function moveMerges(sheet: Sheet, axis: Axis, map: IndexMap): void {
  if (!sheet.merges) return;
  const next: Merge[] = [];
  for (const m of sheet.merges) {
    const lo = axis === 'row' ? m.r1 : m.c1;
    const hi = axis === 'row' ? m.r2 : m.c2;
    const kept: number[] = [];
    for (let i = lo; i <= hi; i++) {
      const moved = map(i);
      if (moved !== null) kept.push(moved);
    }
    if (kept.length === 0) continue;
    const nlo = Math.min(...kept);
    const nhi = Math.max(...kept);
    const moved: Merge = axis === 'row' ? { ...m, r1: nlo, r2: nhi } : { ...m, c1: nlo, c2: nhi };
    if (moved.r1 !== moved.r2 || moved.c1 !== moved.c2) next.push(moved);
  }
  sheet.merges = next.length > 0 ? next : undefined;
}
